import { useState, useEffect } from 'react'
import { Plus, Trash2, Users, Briefcase, Check, Search } from 'lucide-react'
import { db } from '../../config/firebase'
import { collection, addDoc, deleteDoc, doc, query, where, getDocs, serverTimestamp, onSnapshot, getDoc } from 'firebase/firestore'
import { useAuthStore } from '../../store/authStore'
import toast from 'react-hot-toast'

interface Department {
    id: string
    name: string
    responsibleMemberIds: string[]
    createdAt: any
}

export default function DepartmentManager() {
    const { currentOrg } = useAuthStore()
    const [departments, setDepartments] = useState<Department[]>([])
    const [isCreating, setIsCreating] = useState(false)
    const [newName, setNewName] = useState('')
    const [selectedMembers, setSelectedMembers] = useState<string[]>([])
    const [availableMembers, setAvailableMembers] = useState<any[]>([])
    const [loading, setLoading] = useState(false)
    const [memberSearch, setMemberSearch] = useState('')

    // Load Departments
    useEffect(() => {
        if (!currentOrg) return
        const q = query(
            collection(db, 'chat_departments'),
            where('orgId', '==', currentOrg.id)
        )
        const unsub = onSnapshot(q, (snap) => {
            const data = snap.docs.map(d => ({ id: d.id, ...d.data() })) as Department[]
            setDepartments(data)
        })
        return () => unsub()
    }, [currentOrg])

    // Load Members
    useEffect(() => {
        if (!currentOrg) return

        async function load() {
            try {
                // 1. Get Members linked to Org
                const membersRef = collection(db, 'members')
                const q = query(membersRef, where('orgId', '==', currentOrg!.id))
                const snap = await getDocs(q)
                const memberUserIds = snap.docs.map(d => d.data().userId)

                if (memberUserIds.length === 0) {
                    setAvailableMembers([])
                    return
                }

                // 2. Fetch User Profiles
                // Optimization: If list is huge, we should chunk. userDocs implies < 50-100 usually.
                const userDocs = await Promise.all(
                    memberUserIds.map(async (uid) => {
                        try {
                            // Try simple doc fetch first if ID matches
                            const d = await getDoc(doc(db, 'users', uid))
                            if (d.exists()) return { uid, ...d.data() }

                            // Fallback to query
                            const qUser = query(collection(db, 'users'), where('uid', '==', uid))
                            const sUser = await getDocs(qUser)
                            if (!sUser.empty) return { uid, ...sUser.docs[0].data() }

                            return null
                        } catch (e) {
                            return null
                        }
                    })
                )

                // 3. Filter valid
                const validUsers = userDocs
                    .filter(u => u !== null)
                    .map(u => ({
                        id: u!.uid,
                        // @ts-ignore
                        name: u!.displayName || u!.name || u!.email || 'Sem nome'
                    }))

                setAvailableMembers(validUsers)
            } catch (error) {
                console.error("Error loading members:", error)
            }
        }
        load()
    }, [currentOrg])


    const handleCreate = async () => {
        if (!newName || selectedMembers.length === 0) {
            toast.error('Informe o nome e selecione responsáveis')
            return
        }
        setLoading(true)
        try {
            await addDoc(collection(db, 'chat_departments'), {
                orgId: currentOrg?.id,
                name: newName,
                responsibleMemberIds: selectedMembers,
                createdAt: serverTimestamp()
            })
            toast.success('Departamento criado!')
            setNewName('')
            setSelectedMembers([])
            setIsCreating(false)
        } catch (error) {
            toast.error('Erro ao criar')
        } finally {
            setLoading(false)
        }
    }

    const handleDelete = async (id: string) => {
        if (!confirm('Excluir este departamento?')) return
        await deleteDoc(doc(db, 'chat_departments', id))
        toast.success('Excluído')
    }

    const toggleMember = (uid: string) => {
        setSelectedMembers(prev =>
            prev.includes(uid) ? prev.filter(x => x !== uid) : [...prev, uid]
        )
    }

    const filteredMembers = availableMembers.filter(m =>
        m.name.toLowerCase().includes(memberSearch.toLowerCase())
    )

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h3 className="text-lg font-bold text-[#001c28]">Departamentos de Atendimento</h3>
                <button
                    onClick={() => setIsCreating(!isCreating)}
                    className="bg-[#001c28] text-[#d4a94d] px-4 py-2 rounded-xl text-xs font-black uppercase tracking-widest flex items-center gap-2 hover:bg-[#001c28]/90"
                >
                    <Plus size={16} />
                    Novo Setor
                </button>
            </div>

            {isCreating && (
                <div className="bg-[#f8f9fa] p-6 rounded-2xl border border-[#001c28]/5 animate-in slide-in-from-top-2">
                    <div className="space-y-4">
                        <div>
                            <label className="text-xs font-bold text-[#001c28] uppercase">Nome do Setor</label>
                            <input
                                value={newName}
                                onChange={e => setNewName(e.target.value)}
                                placeholder="Ex: Financeiro, Suporte Técnico..."
                                className="w-full mt-1 px-4 py-2 rounded-xl border border-[#001c28]/10 bg-white focus:border-[#d4a94d] outline-none"
                            />
                        </div>

                        <div>
                            <label className="text-xs font-bold text-[#001c28] uppercase mb-2 block">Membros Responsáveis</label>

                            <div className="relative mb-2">
                                <Search className="absolute left-3 top-2.5 text-gray-400" size={14} />
                                <input
                                    value={memberSearch}
                                    onChange={e => setMemberSearch(e.target.value)}
                                    placeholder="Filtrar por nome..."
                                    className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-xl focus:border-[#d4a94d] outline-none"
                                />
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-40 overflow-y-auto border border-gray-100 rounded-xl p-2 bg-white">
                                {filteredMembers.length === 0 ? (
                                    <div className="col-span-full text-center py-4 text-xs text-gray-400">
                                        Nenhum membro encontrado.
                                    </div>
                                ) : filteredMembers.map(m => (
                                    <button
                                        key={m.id}
                                        onClick={() => toggleMember(m.id)}
                                        className={`flex items-center gap-2 p-2 rounded-lg text-xs font-bold transition-all text-left ${selectedMembers.includes(m.id)
                                            ? 'bg-[#001c28] text-white'
                                            : 'bg-white text-gray-500 hover:bg-gray-100'
                                            }`}
                                    >
                                        <div className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${selectedMembers.includes(m.id) ? 'border-[#d4a94d]' : 'border-gray-300'}`}>
                                            {selectedMembers.includes(m.id) && <Check size={10} className="text-[#d4a94d]" />}
                                        </div>
                                        <span className="truncate">{m.name}</span>
                                    </button>
                                ))}
                            </div>
                            <p className="text-[10px] text-gray-400 mt-1 pl-1">
                                {selectedMembers.length} selecionado(s)
                            </p>
                        </div>

                        <div className="flex justify-end gap-2 pt-4 border-t border-gray-100">
                            <button onClick={() => setIsCreating(false)} className="px-4 py-2 text-xs font-bold text-gray-400 hover:text-red-500 transition-colors">Cancelar</button>
                            <button
                                onClick={handleCreate}
                                disabled={loading}
                                className="bg-[#d4a94d] text-[#001c28] px-6 py-2 rounded-xl text-xs font-black uppercase hover:scale-105 transition-transform disabled:opacity-50"
                            >
                                {loading ? 'Salvando...' : 'Salvar Departamento'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {departments.map(dept => (
                    <div key={dept.id} className="bg-white p-5 rounded-2xl border border-[#001c28]/5 shadow-sm flex justify-between items-start group hover:border-[#d4a94d]/30 transition-colors">
                        <div>
                            <div className="flex items-center gap-2 mb-2">
                                <div className="p-2 bg-[#001c28]/5 rounded-lg text-[#001c28]">
                                    <Briefcase size={18} />
                                </div>
                                <h4 className="font-bold text-[#001c28]">{dept.name}</h4>
                            </div>
                            <div className="flex items-center gap-2 text-xs text-gray-500">
                                <Users size={12} />
                                <span>{dept.responsibleMemberIds.length} membros responsáveis</span>
                            </div>
                        </div>
                        <button
                            onClick={() => handleDelete(dept.id)}
                            className="p-2 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                        >
                            <Trash2 size={16} />
                        </button>
                    </div>
                ))}

                {departments.length === 0 && !isCreating && (
                    <div className="col-span-full py-8 text-center text-gray-400 text-sm">
                        Nenhum departamento configurado.
                    </div>
                )}
            </div>
        </div>
    )
}
